import { useContext, useEffect, useMemo, useReducer } from 'react';
import {
  ChatSessionInputContext,
  ChatSessionQueriesContext,
  ActiveChatSessionContext,
  ChatPageContextDataContext,
  ChatSessionSelectedQueryContext,
  ChatSessionFiltersContext,
  ChatSessionFileUploadsContext,
  ChatSessionKolSearchesContext,
  ChatSessionPendingQueryContext,
  ChatSessionPendingHintPromptIdentifierContext,
  ChatSettingsContext,
  ChatSessionItemsContext,
  ChatContextItemsContext,
  ChatLandingCardsVisibleContext,
  ChatSessionPendingAttachmentsContext,
} from '@trinity/chat-components/components/Chat/context';
import {
  ChatHistoryItemsContext,
  ChatHistoryManagerContext,
  ChatHistorySharedContext,
  ChatTabsContext,
} from '@trinity/chat-components/components/Chat.History/context';
import { useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { ChatAgentsContainer } from '@trinity/chat-components/containers/ChatContexts/Chat.Agents.Container';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { buildMessageItems, buildMessageQueries } from '@trinity/chat-components/utils';
import { ChatStateDispatchContext } from './context';
import { chatReducer, generateEmptyChatState } from './state';
import { useActivePageSync } from './useActivePageSync';
import { useChatSettings } from './useChatSettings';

type Props = {
  children: React.ReactNode;
};

const ChatStateContainer = ({ children }: Props) => {
  const features = useGroupFeatures();
  const settings = useChatSettings();
  const brandIdentifier = useActiveBrandIdentifier();
  const contextItems = useContext(ChatContextItemsContext);
  const pageContext = useContext(ChatPageContextDataContext);

  if (!brandIdentifier) {
    throw new Error('No active brand identifier found for ChatStateContainer');
  }

  const [state, dispatch] = useReducer(
    chatReducer,
    generateEmptyChatState({
      context: pageContext,
      contextItems,
      features,
      settings,
      brandIdentifier,
      landingCardsVisible: true,
    })
  );

  const memoizedContext = useMemo(() => pageContext, [pageContext]);
  useActivePageSync(state.session.chatInstance);

  useEffect(() => {
    dispatch({
      type: 'reset-chat-state',
      payload: {
        context: memoizedContext,
        brandIdentifier,
      },
    });
  }, [memoizedContext, dispatch, brandIdentifier]);

  const selectedQuery = useMemo(() => {
    return state.session.queries[state.session.selectedQueryIdentifier];
  }, [state.session.queries, state.session.selectedQueryIdentifier]);

  const queries = useMemo(
    () =>
      buildMessageQueries({
        selectedQueryIdentifier: selectedQuery?.identifier,
        queries: state.session.queries,
      }),
    [selectedQuery?.identifier, state.session.queries]
  );

  const items = useMemo(
    () =>
      buildMessageItems({
        fileUploads: state.session.fileUploads,
        queries,
        kolSearches: state.session.kolSearches,
      }),
    [state.session.fileUploads, state.session.kolSearches, queries]
  );

  const tabsValue = useMemo(() => {
    const allChats = [...state.history.items, ...state.history.shared];
    const chatMap = new Map(allChats.map((chat) => [chat.identifier, chat]));

    const sessionInstance = state.session.chatInstance;

    const resolvedItems = state.history.tabs.items
      .map((identifier) => {
        const chat = chatMap.get(identifier);
        if (chat) {
          return {
            id: chat.id,
            identifier: chat.identifier,
            name: chat.name ? chat.name : 'New Chat',
          };
        }

        if (sessionInstance.identifier === identifier) {
          return {
            id: sessionInstance.id,
            identifier: sessionInstance.identifier,
            name: sessionInstance.name ? sessionInstance.name : 'New Chat',
          };
        }

        return null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    return {
      index: state.history.tabs.index,
      items: resolvedItems,
    };
  }, [
    state.history.tabs.index,
    state.history.tabs.items,
    state.history.items,
    state.history.shared,
    state.session.chatInstance,
  ]);

  return (
    <ChatStateDispatchContext.Provider value={dispatch}>
      <ChatSessionPendingQueryContext.Provider value={state.session.pendingQuery}>
        <ChatSessionPendingHintPromptIdentifierContext.Provider
          value={state.session.pendingHintPromptIdentifier}
        >
          <ChatSessionKolSearchesContext.Provider value={state.session.kolSearches}>
            <ChatSessionQueriesContext.Provider value={state.session.queries}>
              <ChatSessionInputContext.Provider value={state.session.input}>
                <ChatSessionFiltersContext.Provider value={state.session.filters}>
                  <ActiveChatSessionContext.Provider value={state.session.chatInstance}>
                    <ChatSessionSelectedQueryContext.Provider value={selectedQuery}>
                      <ChatHistoryItemsContext.Provider value={state.history.items}>
                        <ChatHistorySharedContext.Provider value={state.history.shared}>
                          <ChatHistoryManagerContext.Provider value={state.history.manager}>
                            <ChatTabsContext.Provider value={tabsValue}>
                              <ChatSessionFileUploadsContext.Provider value={state.session.fileUploads}>
                                <ChatSettingsContext.Provider value={settings}>
                                  <ChatSessionItemsContext.Provider value={items}>
                                    <ChatLandingCardsVisibleContext.Provider
                                      value={state.session.landingCardsVisible}
                                    >
                                      <ChatSessionPendingAttachmentsContext.Provider
                                        value={state.session.pendingAttachments}
                                      >
                                        {children}
                                      </ChatSessionPendingAttachmentsContext.Provider>
                                    </ChatLandingCardsVisibleContext.Provider>
                                  </ChatSessionItemsContext.Provider>
                                </ChatSettingsContext.Provider>
                              </ChatSessionFileUploadsContext.Provider>
                            </ChatTabsContext.Provider>
                          </ChatHistoryManagerContext.Provider>
                        </ChatHistorySharedContext.Provider>
                      </ChatHistoryItemsContext.Provider>
                    </ChatSessionSelectedQueryContext.Provider>
                  </ActiveChatSessionContext.Provider>
                </ChatSessionFiltersContext.Provider>
              </ChatSessionInputContext.Provider>
            </ChatSessionQueriesContext.Provider>
          </ChatSessionKolSearchesContext.Provider>
        </ChatSessionPendingHintPromptIdentifierContext.Provider>
      </ChatSessionPendingQueryContext.Provider>
    </ChatStateDispatchContext.Provider>
  );
};

const StateWithContext = (props: Props) => {
  return (
    <ChatAgentsContainer>
      <ChatStateContainer>{props.children}</ChatStateContainer>
    </ChatAgentsContainer>
  );
};

export { StateWithContext as ChatStateContainer };
